<script>
import useVuelidate from '@vuelidate/core';
import {helpers, maxLength, required, requiredIf} from '@vuelidate/validators';

/* COMPOSANTS */
import AkFormSubmitted from '@components/general/AkFormSubmitted';
import AkDropdown from '@components/v2/input/AkDropdown';
import AkInputMoney from '@components/v2/input/AkInputMoney';
import AkInputText from '@components/v2/input/AkInputText';
import AkAddressFull from '@components/v2/input/AkAddressFull';
import AkPictureAccommodation from '@components/v2/general/AkPictureAccommodation';
import AkButtonValidation from '@components/v2/general/AkButtonValidation';
import HousekeeperPrestationForm from '@views/housekeepers/HousekeeperPrestationForm';
import GaView from '@components/v2/layout/GaView';
import AkInputPhone from '@components/v2/input/AkInputPhone.vue';

/* MIXINS */
import utilsMixin from '@mixins/utilsMixin';
import permissionsMixin from '@mixins/permissionsMixin';
import messageMixin from '@mixins/messageMixin';
import backMixin from '@mixins/backMixin';
import companyMixin from '@mixins/companyMixin';
import formatMixin from '@mixins/formatMixin';
import scrollToTopMixin from '@mixins/scrollToTopMixin';
import civilitiesConst from '@mixins/const/civilitiesConst';
import prestationTypesConst from '@mixins/const/prestationTypesConst';
import housekeeperFormMixin from '@views/housekeepers/mixins/housekeeperFormMixin';

/* SERVICES */
import housekeeperService from '@services/housekeeperService';
import itemLabelService from '@services/itemLabelService';
import housekeeperPrestationService from '@services/housekeeperPrestationService';
import housekeeperAccommodationService from '@services/housekeeperAccommodationService';

export default {
  components: {
    GaView,
    AkButtonValidation,
    HousekeeperPrestationForm,
    AkPictureAccommodation,
    AkAddressFull,
    AkInputText,
    AkInputMoney,
    AkDropdown,
    AkFormSubmitted,
    AkInputPhone,
  },
  mixins: [
    utilsMixin,
    permissionsMixin,
    messageMixin,
    companyMixin,
    formatMixin,
    backMixin,
    scrollToTopMixin,
    civilitiesConst,
    prestationTypesConst,
    housekeeperFormMixin,
  ],
  setup() {
    return {v$: useVuelidate()};
  },
  head() {
    return {
      title: 'housekeeper.create',
    };
  },
  validations() {
    return {
      housekeeper: {
        companyId: {required: requiredIf(this.isGas() || this.isAdmin())},
        iban: {required, ibanValidator: helpers.withMessage('Vérifier le format du numéro IBAN', this.ibanValidator)},
        bic: {required, bicValidator: helpers.withMessage('Vérifier le format du numéro BIC', this.bicValidator)},
        contact: {
          companyName: {required},
          formeJuridiqueId: {required},
          raisonSociale: {required},
          email: {
            required,
            maxLengthValue: maxLength(100),
            email: helpers.withMessage(
              "Le format de l'adresse e-mail est invalide. Veuillez vérifier et réessayer (ex. : nom@domaine.com)",
              this.emailValidator,
            ),
          },
          phone: {required, phoneValidator: this.phoneValidator},
          civility: {required},
          firstName: {
            required,
            maxLengthValue: maxLength(100),
            customPattern: helpers.withMessage(this.$t('bad_text_format'), this.yousignValidator),
          },
          lastName: {
            required,
            maxLengthValue: maxLength(100),
            customPattern: helpers.withMessage(this.$t('bad_text_format'), this.yousignValidator),
          },
        },
      },
    };
  },
  data() {
    return {
      submitted: false,
      housekeeper: {
        companyId: null,
        accommodations: [],
        accommodationIds: [],
        contact: {
          companyName: null,
          formeJuridiqueId: null,
          raisonSociale: null,
          capital: null,
          address: null,
          complement: null,
          postalCode: null,
          city: null,
          state: null,
          country: null,
          email: null,
          phone: null,
          villeRcs: null,
          siret: null,
          civility: null,
          lastName: null,
          firstName: null,
          function: null,
        },
      },
    };
  },
  mounted() {
    this.initGoBack();

    itemLabelService.itemLabels({type: 'FORME_JURIDIQUE'}).then((data) => {
      this.listFormeJuridique = data;
    });
  },
  methods: {
    createHousekeeper() {
      this.housekeeper.accommodationIds = this.selectedAccommodationIds;
      this.housekeeper.housekeeperPrestations = this.housekeeperPrestations;

      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.btnCreate.init();
        this.addError(this.$t('error.check_fields'));
        return;
      }

      housekeeperService
        .create(this.housekeeper)
        .then(() => {
          this.success(this.$t('housekeeper.added'));
          this.initHousekeeper();
          this.scrollToTop();
        })
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        })
        .finally(() => {
          this.submitted = false;
          this.$refs.btnCreate.init();
        });
    },
    initHousekeeper() {
      this.housekeeper = {
        companyId: null,
        accommodations: [],
        accommodationIds: [],
        contact: {
          companyName: null,
          formeJuridiqueId: null,
          raisonSociale: null,
          capital: null,
          address: null,
          complement: null,
          postalCode: null,
          city: null,
          state: null,
          country: null,
          email: null,
          phone: null,
          villeRcs: null,
          siret: null,
          civility: null,
          lastName: null,
          firstName: null,
          function: null,
        },
      };
    },

    // PARTNER MISSIONS
    getPartnerMissions(companyId) {
      const filter = {
        companyId: companyId,
      };

      housekeeperPrestationService.housekeeperPrestations(filter).then((data) => {
        this.partnerMissions = data;
      });
    },
  },
  computed: {},
  watch: {
    'housekeeper.companyId'(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.housekeeperAccommodations = [];
      housekeeperAccommodationService.housekeeperAccommodations({companyId: newVal}).then((data) => {
        this.housekeeperAccommodations = data;

        this.housekeeperAccommodations.forEach((ha) => {
          if (ha.housekeeperId != null) ha.disabled = true;
          if (this.housekeeper.accommodationIds.includes(ha.accommodationId)) ha.isSelected = true;
        });
      });

      this.getPartnerMissions(newVal);
    },
  },
};
</script>

<template>
  <GaView details>
    <template #content>
      <AkFormSubmitted :disabled="false" :submitted="submitted" class="flex flex-col md:gap-4 gap-2" reference="form">
        <span class="title-border secondary text-grey-900 text-xl font-bold mb-3">Contact</span>
        <div class="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 md:gap-4 gap-2">
          <AkDropdown
            v-model="housekeeper.contact.formeJuridiqueId"
            :label="$t('contact.formeJuridique')"
            :loading="isLoadingFormeJuridique"
            :options="this.listFormeJuridique"
            :validator="v$.housekeeper.contact.formeJuridiqueId"
            optionLabel="label"
            optionValue="id" />
          <AkInputText
            v-model="housekeeper.contact.raisonSociale"
            :label="$t('contact.raisonSociale')"
            :formatter="toUppercase"
            :validator="v$.housekeeper.contact.raisonSociale" />
          <AkInputText
            v-model="housekeeper.contact.companyName"
            :label="$t('contact.companyName')"
            :validator="v$.housekeeper.contact.companyName" />
          <AkInputMoney v-model="housekeeper.contact.capital" label="Capital (€)" />
        </div>

        <div class="grid md:grid-cols-2 grid-cols-1 md:gap-4 gap-2">
          <AkInputText
            v-model="housekeeper.contact.villeRcs"
            :label="$t('contact.villeRcs')"
            :formatter="toUppercase" />
          <AkInputText v-model="housekeeper.contact.siret" :label="$t('contact.siret')" />
        </div>

        <div class="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 md:gap-4 gap-2">
          <AkDropdown
            v-model="housekeeper.contact.civility"
            :label="$t('contact.civilityManager')"
            :options="this.getCivilitiesConstForProspect()"
            :validator="v$.housekeeper.contact.civility" />
          <AkInputText
            v-model="housekeeper.contact.lastName"
            :label="$t('contact.lastNameManager')"
            :formatter="toUppercase"
            :validator="v$.housekeeper.contact.lastName" />
          <AkInputText
            v-model="housekeeper.contact.firstName"
            :label="$t('contact.firstNameManager')"
            :validator="v$.housekeeper.contact.firstName" />
          <AkInputText v-model="housekeeper.contact.function" :label="$t('contact.functionManager')" />
        </div>

        <div class="grid md:grid-cols-2 grid-cols-1 md:gap-4 gap-2">
          <AkInputText
            v-model="housekeeper.contact.email"
            :label="$t('contact.email')"
            :validator="v$.housekeeper.contact.email"
            icon="ga-icon ga-email" />
          <AkInputPhone
            v-model="housekeeper.contact.phone"
            :label="$t('contact.phone')"
            :validator="v$.housekeeper.contact.phone" />
        </div>

        <span class="title-border secondary text-grey-900 text-xl font-bold mb-3">{{
          $t('client.title_address')
        }}</span>
        <AkAddressFull
          v-model="housekeeper.contact"
          :always-display-map="false"
          :can-change-position="false"
          :label="$t('contact.address1Manager')"
          :validator="v$.housekeeper" />

        <span v-if="isGas() || isAdmin()" class="title-border secondary text-grey-900 text-xl font-bold mb-3"
          >Informations complémentaires</span
        >

        <div class="flex md:flex-row flex-col gap-2 w-full">
          <AkInputText v-model="housekeeper.iban" :label="$t('mandat.iban')" :validator="v$.housekeeper.iban" />
          <AkInputText v-model="housekeeper.bic" :label="$t('mandat.bic')" :validator="v$.housekeeper.bic" />
        </div>

        <div class="title-border secondary text-grey-900 text-xl font-bold mb-3">
          Logements
          <div
            class="flex flex-col lg:flex-row md:gap-4 gap-2 ml-4"
            v-if="housekeeperAccommodations && housekeeperAccommodations.length > 0 && housekeeper.companyId">
            <span class="xs btn-grey" @click="selectAllAccommodation()">
              <i class="ga-icon ga-check_circle mr-1" /> {{ $t('select_all') }}
            </span>
            <span class="xs btn-grey" @click="unselectAllAccommodation()">
              <i class="ga-icon ga-close_circle mr-1" /> {{ $t('unselect_all') }}
            </span>
          </div>
        </div>
        <div v-if="isGas() || isAdmin()" class="grid md:grid-cols-2 grid-cols-1 md:gap-4 gap-2">
          <AkDropdown
            v-model="housekeeper.companyId"
            :options="companies"
            :validator="v$.housekeeper.companyId"
            label="Agence"
            option-label="name"
            option-value="id" />
          <AkInputText v-model="this.filterName" label="Nom du logement" />
        </div>

        <div class="grid 2xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 grid-cols-1 md:gap-4 gap-2">
          <div v-for="ha in housekeeperAccommodationsWithFilter" :key="ha.accommodationId">
            <AkPictureAccommodation
              :id="ha.accommodationId"
              :class="{
                '!border-[#0d6be9]': ha.isSelected,
                'opacity-50': ha.housekeeperId != null,
              }"
              :md5="ha.accommodationPicture"
              :name="ha.accommodationName"
              :physical="isReceptionTypePhysical(ha)"
              class="border-2 border-transparent"
              cover
              radius="rounded-t-lg"
              :height="134"
              disabledLink
              @click="toggleAccommodation(ha)" />
            <div v-if="ha.isSelected" class="rounded-r-md p-4 bg-white">
              <HousekeeperPrestationForm
                v-model="housekeeperPrestations"
                :index="getHousekeeperPrestationIndex(ha)"
                :partnerMissionTypes="getPartnerMissionTypesForForm(ha.accommodationId)" />
            </div>
          </div>
        </div>
        <div class="flex justify-end mt-2">
          <AkButtonValidation ref="btnCreate" :label="$t('add')" class="btn-primary" @action="createHousekeeper" />
        </div>
      </AkFormSubmitted>
    </template>
  </GaView>
</template>
